'use client';

import React, { useCallback, useEffect, useState } from 'react';

import { ThemeToggle } from '@theme';
import LanguageToggle from './LanguageToggle';
import HeaderLogo from './HeaderLogo';
import MenuToggle from './MenuToggle';
import HeaderNav, { type HeaderNavSection } from './HeaderNav';
import { HEADER_NAV_REGION_ID } from './navRegion';

type HeaderNavRenderArgs = {
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
  toggleMenu: () => void;
  navRegionId: typeof HEADER_NAV_REGION_ID;
  locale: 'en' | 'es';
  currentPath: string;
  navSections?: HeaderNavSection[];
  resolveNavHref?: (href: string, locale: 'en' | 'es') => string;
};

type HeaderProps = {
  initialTheme?: 'light' | 'dark';
  appendNav?: React.ReactNode;
  locale?: 'en' | 'es';
  /** Logo wordmark — pass global `site_name`. */
  siteNameLabel?: string;
  /** Fallback logo text when `siteNameLabel` is omitted (e.g. `site_owner`). */
  siteOwnerLabel?: string;
  /** Optional mark inside `.logo-icon` (e.g. site-specific SVG); defaults to `HeaderLogoSVG`. */
  logoSvg?: React.ReactNode;
  headerHref?: string;
  currentPath?: string;
  navSections?: HeaderNavSection[];
  resolveNavHref?: (href: string, locale: 'en' | 'es') => string;
  showLanguageToggle?: boolean;
  onToggleLanguage?: () => void;
  languageToggleAriaLabelEn?: string;
  languageToggleAriaLabelEs?: string;
  onResolvedThemeChange?: (theme: 'light' | 'dark') => void;
  /**
   * Replace the default `<HeaderNav />` with a static tree. Gets the same outer `<nav id={navRegionId} className="nav-links …">` wrapper and `menuOpen` CSS class as the built-in nav so the mobile menu toggle keeps working for typical layouts.
   * Prefer `renderNav` when the slot needs `menuOpen` / `setMenuOpen` / locale-driven props.
   */
  nav?: React.ReactNode;
  /**
   * Full control over the nav region. When provided, takes precedence over `nav` and the default `HeaderNav`.
   * Use for child apps that supply their own markup without forking `Header`.
   */
  renderNav?: (args: HeaderNavRenderArgs) => React.ReactNode;
  /** Controlled mobile nav drawer. When set, `Header` calls `onMenuOpenChange` instead of owning state. */
  menuOpen?: boolean;
  /** Uncontrolled initial open state (ignored when `menuOpen` is defined). */
  defaultMenuOpen?: boolean;
  onMenuOpenChange?: (open: boolean) => void;
  /**
   * Optional CTAs (e.g. Login / Join) rendered inside `.nav-ctas` before the theme toggle.
   * Keep modals outside `Header` (e.g. in `template.tsx`); wire buttons here via shell state.
   */
  ctaSlot?: React.ReactNode;
};

const Header: React.FC<HeaderProps> = ({
  appendNav,
  locale = 'en',
  siteNameLabel,
  siteOwnerLabel,
  logoSvg,
  headerHref,
  currentPath = '/',
  navSections,
  resolveNavHref,
  showLanguageToggle = true,
  onToggleLanguage,
  languageToggleAriaLabelEn,
  languageToggleAriaLabelEs,
  onResolvedThemeChange,
  nav,
  renderNav,
  menuOpen: menuOpenControlled,
  defaultMenuOpen = false,
  onMenuOpenChange,
  ctaSlot,
}) => {
  const isMenuControlled = menuOpenControlled !== undefined;
  const [menuOpenInternal, setMenuOpenInternal] = useState(defaultMenuOpen);
  const menuOpen = isMenuControlled ? menuOpenControlled : menuOpenInternal;

  const setMenuOpen = useCallback(
    (next: boolean) => {
      if (!isMenuControlled) {
        setMenuOpenInternal(next);
      }
      onMenuOpenChange?.(next);
    },
    [isMenuControlled, onMenuOpenChange]
  );

  const toggleMenu = useCallback(() => {
    setMenuOpen(!menuOpen);
  }, [menuOpen, setMenuOpen]);

  const handleMenuToggle = () => {
    toggleMenu();
  };

  const navRenderArgs: HeaderNavRenderArgs = {
    menuOpen,
    setMenuOpen,
    toggleMenu,
    navRegionId: HEADER_NAV_REGION_ID,
    locale,
    currentPath,
    navSections,
    resolveNavHref,
  };

  const navRegion = (() => {
    if (renderNav) {
      return renderNav(navRenderArgs);
    }
    if (nav != null) {
      return (
        <nav id={HEADER_NAV_REGION_ID} className={`nav-links${menuOpen ? ' open' : ''}`}>
          {nav}
        </nav>
      );
    }
    return (
      <HeaderNav
        menuOpen={menuOpen}
        sections={navSections}
        currentPath={currentPath}
        locale={locale}
        resolveLinkHref={resolveNavHref}
      />
    );
  })();

  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setHidden(window.scrollY > 500);
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className={`header${hidden ? ' header--hidden' : ''}`}>
      <div className='container nav'>
        <HeaderLogo
          locale={locale}
          href={headerHref}
          siteNameLabel={siteNameLabel}
          siteOwnerLabel={siteOwnerLabel}
          logoSvg={logoSvg}
        />
        {navRegion}
        <div className='nav-ctas'>
          {showLanguageToggle ? (
            <LanguageToggle
              isSpanish={locale === 'es'}
              onToggle={onToggleLanguage}
              ariaLabelEn={languageToggleAriaLabelEn}
              ariaLabelEs={languageToggleAriaLabelEs}
            />
          ) : null}
          {ctaSlot}
          <ThemeToggle onResolvedThemeChange={onResolvedThemeChange} />
          <MenuToggle
            onClick={handleMenuToggle}
            ariaControls={HEADER_NAV_REGION_ID}
            ariaExpanded={menuOpen}
          />
        </div>
      </div>
      {appendNav}
    </header>
  );
};

export type { HeaderNavRenderArgs, HeaderProps };
export { Header };
export { HEADER_NAV_REGION_ID } from './navRegion';
